import type { ReactNode } from 'react';
import { isForbidden } from '@cwos/api-client';
import type { dashboard } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { LoadFailure } from '../../LoadFailure.js';
import { askPhibian } from '../../intelligence/global/index.js';
import { usePermissions } from '../../permissions.js';
import type { MetricAbout } from './explain.js';
import { changeLabel, changeTone, formatMetricValue, periodRangeLabel } from './analyticsRange.js';
import {
  disclosureBanner,
  NOT_MEASURED_LABEL,
  SUPPRESSED_LABEL,
  suppressionReason,
  type MeasureDisplay,
} from './disclosure.js';

/**
 * Shared furniture for the analytics views — stat tiles, the headline KPI card,
 * a panel wrapper, and a reusable drill-down table. Kept local to avoid
 * perturbing the existing Executive Dashboard widgets.
 */

/**
 * A structural view of a react-query result — avoids the `useQuery<…>` generic
 * dance.
 *
 * `error` IS PART OF THE SHAPE, not an optional extra. It was omitted, and the
 * omission is the reason every analytics view rendered a 403 as "we couldn't
 * load this just now" with a Try again under it: the type these views handed
 * around had `isError` and no way to ask WHY, so the only sentence they could
 * write was the one that fits every failure equally badly. A boolean cannot
 * distinguish a refusal from an outage, and a `QueryLike` that carries only the
 * boolean quietly guarantees no caller ever will.
 */
export type QueryLike<T> = {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
  refetch: () => void;
};

/**
 * A stat tile, which OPENS when `onOpen` is given: the records behind the
 * figure and what it means (`DrillPanel`). A figure with no rows of its own
 * stays a plain tile.
 */
function StatShell({ onOpen, children }: { onOpen?: () => void; children: ReactNode }) {
  if (!onOpen) return <div className="db-stat">{children}</div>;
  return (
    <button
      type="button"
      className="db-stat"
      onClick={onOpen}
      style={{ textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
    >
      {children}
    </button>
  );
}

export function Stat({
  label,
  value,
  sub,
  onOpen,
}: {
  label: string;
  value: string;
  sub?: string;
  onOpen?: () => void;
}) {
  return (
    <StatShell onOpen={onOpen}>
      <div className="db-stat-value">{value}</div>
      <div className="db-stat-label">{label}</div>
      {sub ? (
        <div className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
          {sub}
        </div>
      ) : null}
    </StatShell>
  );
}

/**
 * A stat tile fed by `disclosure.measure()`.
 *
 * The point of routing suppressed tiles through here is that the value and the
 * reason are ONE argument: there is no way to render the blank without also
 * rendering why it is blank. A tile reading `Hidden` with nothing beside it is
 * how somebody concludes the product is broken.
 */
export function MeasureStat({
  label,
  display,
  sub,
  onOpen,
}: {
  label: string;
  display: MeasureDisplay;
  sub?: string;
  onOpen?: () => void;
}) {
  return (
    <StatShell onOpen={display.state === 'value' ? onOpen : undefined}>
      <div
        className="db-stat-value"
        style={display.state === 'value' ? undefined : { color: 'var(--color-soft)' }}
      >
        {display.value}
      </div>
      <div className="db-stat-label">{label}</div>
      {(display.note ?? sub) ? (
        <div className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
          {display.note ?? sub}
        </div>
      ) : null}
    </StatShell>
  );
}

/**
 * A figure with NO SOURCE: "Not measured" and the reason, never a 0. For a
 * time-clock figure in an organization with no time clock, and for a rate with
 * no defensible denominator.
 */
export function NotMeasuredStat({ label, reason }: { label: string; reason: string }) {
  return (
    <div className="db-stat">
      <div className="db-stat-value" style={{ color: 'var(--color-soft)' }}>
        {NOT_MEASURED_LABEL}
      </div>
      <div className="db-stat-label">{label}</div>
      <div className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
        {reason}
      </div>
    </div>
  );
}

/**
 * "Ask Phibian about this", with the question already written — opens the one
 * global Ask (`askPhibian`) with it drafted; the person sends it. Rendered only
 * for a reader holding `intelligence:use`, the launcher's own gate, so there
 * is no button that opens nothing.
 */
export function AskAbout({ question }: { question: string }) {
  const { has } = usePermissions();
  if (!has('intelligence:use')) return null;
  return (
    <button type="button" className="rn-btn-quiet" onClick={() => askPhibian(question)}>
      Ask Phibian about this
    </button>
  );
}

/**
 * WHAT THIS FIGURE MEANS — what it counts, where it comes from, what changed
 * and what needs attention, in plain words built from the data on screen
 * (`explain.ts`), plus the Ask Phibian handoff for the "why".
 */
export function Explain({
  about,
  change,
  attention,
  ask,
  open = false,
}: {
  about: MetricAbout;
  change?: string | null;
  attention?: string | null;
  ask?: string;
  /** Start expanded (the drill panel); a chart's note starts folded. */
  open?: boolean;
}) {
  const rows: [string, string][] = [
    ['What it counts', about.counts],
    ['Source', about.source],
    ...(change ? [['What changed', change] as [string, string]] : []),
    ...(attention ? [['Needs attention', attention] as [string, string]] : []),
  ];
  return (
    <details open={open} style={{ marginTop: 'var(--space-8)' }}>
      <summary className="rn-muted" style={{ cursor: 'pointer' }}>
        What this shows
      </summary>
      <dl style={{ margin: 'var(--space-6) 0 0', display: 'grid', gap: 'var(--space-6)' }}>
        {rows.map(([term, text]) => (
          <div key={term}>
            <dt style={{ fontWeight: 600 }}>{term}</dt>
            <dd className="rn-muted" style={{ margin: 0 }}>
              {text}
            </dd>
          </div>
        ))}
      </dl>
      {ask ? (
        <div style={{ marginTop: 'var(--space-8)' }}>
          <AskAbout question={ask} />
        </div>
      ) : null}
    </details>
  );
}

/**
 * A plain-language note standing where data would have been. Used for both
 * kinds of deliberate absence — a withheld section and a suppressed figure —
 * so the two never read as an error or as "no data".
 *
 * `role="note"` and not `role="alert"`: nothing has gone wrong. The reader is
 * being told what they are looking at.
 */
export function DisclosureNote({
  text,
  heading,
  row = false,
}: {
  text: string;
  heading?: string;
  /** Lay the heading beside the text — for the once-per-screen banner. */
  row?: boolean;
}) {
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        flexDirection: row ? 'row' : 'column',
        alignItems: row ? 'center' : undefined,
        flexWrap: row ? 'wrap' : undefined,
        gap: row ? 'var(--space-10)' : 'var(--space-4)',
        padding: 'var(--space-10) var(--space-14)',
        border: '1px solid var(--color-line)',
        // Sky, not amber: nothing is wrong here. An amber strip would tell the
        // reader to act on something they cannot act on.
        borderLeft: '3px solid var(--color-sky)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      {heading ? <span style={{ fontWeight: 600 }}>{heading}</span> : null}
      <span className="rn-muted">{text}</span>
    </div>
  );
}

/** The once-per-screen banner explaining a de-identified payload. */
export function DisclosureStrip({
  disclosure,
}: {
  disclosure: dashboard.AnalyticsDisclosure | undefined;
}) {
  const text = disclosureBanner(disclosure);
  if (!text) return null;
  return <DisclosureNote row heading="De-identified view" text={text} />;
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="db-stats">{children}</div>;
}

/** An ISO instant as the reader's locale writes it, or the raw value if it is not a date. */
export function formatSyncInstant(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString();
}

/** A slim, non-dismissible integration-staleness banner (doc 04 §23). */
export function StalenessStrip({
  staleness,
}: {
  staleness: dashboard.StalenessBanner | null | undefined;
}) {
  if (!staleness) return null;
  return (
    <div className="an-stale" role="note">
      <Pill variant="look" dot>
        Stale data
      </Pill>
      <span className="rn-muted">
        {staleness.message}
        {/* PH-UX-005: `lastSyncAt` is an ISO instant off the wire; it was
            rendered raw ("2026-09-16T05:58:56.030Z"). */}
        {staleness.lastSyncAt ? ` Last sync ${formatSyncInstant(staleness.lastSyncAt)}.` : ''}
      </span>
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">{title}</h2>
        {subtitle ? <span className="rn-muted">{subtitle}</span> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * ONE HEADLINE METRIC, WHICH CAN NOW BE WITHHELD — OR NEVER MEASURED AT ALL.
 *
 * `metric.current` is `null` for one of TWO reasons, and the card must tell
 * them apart rather than print one blank for both:
 *
 *   - the cohort floor removed it — the overview's `hoursWorked` stands on the
 *     worker cohort and `appointments` on the client cohort, so in a small
 *     organization either can be one person's figure;
 *   - `metric.unavailableReason` is set — the metric has no connected source
 *     at all (`time_session` has never recorded a row for this tenant), a
 *     fact about the ORGANIZATION rather than about who is asking or how few
 *     people stand behind it.
 *
 * Either way the card must not print "0h" (a real measurement) or the em dash
 * it uses for an absent PRIOR (which says the previous window had no data).
 *
 * `disclosure` is optional so the component stays usable where no envelope
 * exists; when the value is null, has no `unavailableReason`, and the
 * envelope is missing, the reason falls back to the floor-less sentence
 * rather than being invented here.
 */
export function KpiCard({
  metric,
  disclosure,
  period,
  onClick,
}: {
  metric: dashboard.HeadlineMetric;
  disclosure?: dashboard.AnalyticsDisclosure;
  /**
   * THE WINDOW THIS CARD'S OWN NUMBER WAS COMPUTED OVER, STATED ON THE CARD.
   *
   * The Dashboard's configurable widgets already state theirs inline
   * (`WidgetGrid` prints the saved layout's period once above its grid, and
   * every widget in it shares that one period); the Analytics workspace used
   * to say its period only once, as page-level context above every view. A
   * reader comparing a Dashboard tile against this card had no way to see,
   * on the numbers themselves, whether the two answered the same question —
   * a KPI card and a Dashboard widget can name the same metric over two
   * different windows and look like they disagree about a fact when they are
   * really answering two different questions. Optional so the component
   * still renders wherever a caller has not resolved a period (there is none
   * today; every KpiCard caller has one).
   */
  period?: dashboard.PeriodLabel;
  onClick?: () => void;
}) {
  // `formatMetricValue` already existed and already grouped thousands and
  // honoured the metric's own precision; this card was building its strings by
  // hand instead, so the headline figure on the analytics pulse read "4218.5h"
  // — five digits a person has to count to place the magnitude of.
  const blank = metric.current == null;
  const notMeasured = metric.unavailableReason != null;
  const value = !blank
    ? formatMetricValue(metric.current!, metric.unit, metric.precision)
    : notMeasured
      ? NOT_MEASURED_LABEL
      : SUPPRESSED_LABEL;
  // A BLANK METRIC HAS NO COMPARISON EITHER. `prior` and `changePct` are
  // nulled with `current` by the server for BOTH reasons above — a published
  // prior beside a withheld or unmeasured current hands the reader the figure
  // one week late, or a comparison against a period that was never measured.
  const prior =
    metric.prior == null ? '—' : formatMetricValue(metric.prior, metric.unit, metric.precision);
  return (
    <button
      type="button"
      className="db-kpi"
      onClick={onClick}
      style={{ textAlign: 'left', font: 'inherit', cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="db-kpi-label">{metric.label}</div>
      {period ? (
        <div className="rn-muted" style={{ fontSize: '0.8em' }}>
          {periodRangeLabel(period)}
        </div>
      ) : null}
      <div className="db-kpi-value" style={blank ? { color: 'var(--color-soft)' } : undefined}>
        {value}
      </div>
      {/*
        THE REASON, OR THE COMPARISON — NEVER BOTH, AND NEVER NEITHER. A blank
        card has no prior and no change to show (the server nulls them
        together for either reason), so the footer that would render
        "prior —" beside a neutral pill is replaced by the sentence explaining
        the blank. Leaving the old footer under "Hidden"/"Not measured" would
        read as a rendering fault.
        `metric.unavailableMessage` IS RENDERED VERBATIM, never re-derived —
        the same rule `UnavailableData.message` follows on the dashboard-widget
        surface, so the two surfaces cannot word the same blank differently.
      */}
      {blank ? (
        <div className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
          {notMeasured
            ? metric.unavailableMessage
            : suppressionReason(disclosure?.suppression?.minCohortSize)}
        </div>
      ) : (
        <div className="db-kpi-foot">
          <span className="rn-muted">prior {prior}</span>
          <Pill variant={changeTone(metric.direction)} dot>
            {changeLabel(metric.changePct, metric.direction)}
          </Pill>
        </div>
      )}
    </button>
  );
}

export function DrilldownTable<Row>({
  rows,
  isLoading,
  isError,
  error,
  onRetry,
  columns,
  rowKey,
  emptyHeading,
  emptyMessage,
  withheld,
  onRowOpen,
}: {
  rows: Row[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * The error itself, so a 403 can be told from a 500.
   *
   * Optional because four call sites pass `isError={false}` literally — their
   * parent resolved the query already — and those cannot supply one. Where a
   * real query IS passed in, omitting this means a refusal renders as a
   * retryable failure, which is what every caller here used to do.
   */
  error?: unknown;
  onRetry: () => void;
  columns: Column<Row>[];
  rowKey: (row: Row) => string;
  emptyHeading?: string;
  emptyMessage?: string;
  /**
   * The caller KNOWS the server would refuse, and did not ask (F159).
   *
   * `CareGapsView` stopped firing its clients probe for a reader without
   * `dashboard:read` — the same key table `AnalyticsShell` hides the
   * documentation tab with (F148). A disabled query never errors, so without
   * this flag the withheld state would render as an eternal skeleton. One
   * variable at the call site drives both the query's `enabled` and this
   * prop, so they cannot disagree; whenever a request IS made, the server's
   * answer — the `isForbidden` branch below — remains the authority.
   */
  withheld?: boolean;
  /** Open the record a row stands for (a visit, a worker's schedule). */
  onRowOpen?: (row: Row) => void;
}) {
  if (withheld || (isError && isForbidden(error))) {
    return (
      <Refused
        heading="These details are not shown to you"
        message="Your account does not have access to the rows behind this figure. This is not a count of none."
      />
    );
  }
  if (isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (isError && !rows) {
    return (
      <LoadFailure
        error={error}
        message="We couldn’t load these details just now, so read this as unknown rather than empty."
        onRetry={onRetry}
      />
    );
  }
  // `rows ?? []` then `.length === 0` reported a query that has NOT ANSWERED —
  // no data, not loading, not errored, which is how react-query describes a
  // disabled one — as an affirmative "nothing here". The empty state now needs
  // a real array to have arrived.
  if (rows === undefined) {
    return <SkeletonSet surface="table" rows={4} />;
  }
  if (rows.length === 0) {
    return (
      <SuccessEmpty
        heading={emptyHeading ?? 'Nothing here'}
        message={emptyMessage ?? 'No rows match right now.'}
      />
    );
  }
  return <TableCard columns={columns} rows={rows} rowKey={rowKey} onRowOpen={onRowOpen} />;
}
